/**
 * @file src/core/settings.js
 * 6 tuỳ chọn của bảng CÀI ĐẶT dùng chung:
 *   sound · volume · lang · background · reducedMotion · motionSpeed
 *
 * Luật cho dev tiết: CHỈ gọi setDefault(). Không tự set scene.background,
 * không tự mute audio — shell quản. Xem AGENT.md mục 4.4.
 *
 * `motionSpeed` (0.2..2.0) là HỆ SỐ NHÂN, không phải tốc độ tuyệt đối: tiết tự
 * chọn tốc độ cơ sở của mình rồi nhân với hệ số này. Nhờ vậy một thanh trượt
 * duy nhất điều khiển được mọi loại chuyển động ở mọi tiết.
 * `volume` (0..1) độc lập với `sound`: tắt tiếng KHÔNG làm mất mức âm lượng
 * đã chọn, bật lại là về đúng mức cũ.
 */

const LS_KEY = 'onluyen3d:settings';

/**
 * @typedef {'sound' | 'volume' | 'lang' | 'background' | 'reducedMotion' | 'motionSpeed'
 *   | 'rotate'} SettingKey
 */

/**
 * @typedef {Object} Settings
 * @property {(key: SettingKey, value: any) => void} setDefault  Tiết khai mặc định (không ghi đè lựa chọn của học sinh)
 * @property {(key: SettingKey) => any} get
 * @property {(key: SettingKey, value: any, opts?: {persist?: boolean}) => void} set
 * @property {(key: SettingKey, cb: (v: any) => void) => () => void} onChange
 * @property {() => Record<string, any>} all
 */

/**
 * @param {{ lang?: string, emitter: import('./events.js').Emitter }} opts
 * @returns {Settings}
 */
export function createSettings(opts) {
  /** @type {Record<string, any>} */
  const defaults = {
    sound: true,
    volume: 0.7,
    lang: opts.lang ?? 'vi',
    // XÁM SÁNG là mặc định của hệ thống: nếu config.js của tiết không khai màu nền
    // thì ra màu này, không phải nền tối. Tiết muốn khác thì khai colors.background.
    // #f9fafb = gray-50, đúng màu stage của bản thiết kế B2-Cell-Viewer. Không dùng
    // #ffffff: chrome là THẺ TRẮNG, đặt trên nền trắng tuyệt đối thì mất hẳn mép.
    background: '#f9fafb',
    reducedMotion: matchMedia('(prefers-reduced-motion: reduce)').matches,
    motionSpeed: 1,
    // Xoay tự động. Nút xoay ở taskbar VÀ hàng "Xoay" trong bảng cài đặt đều
    // đọc/ghi đúng khoá này — không có state cục bộ nào ở hai bên.
    rotate: true,
  };

  /** @type {Record<string, any>} */
  let stored = {};
  try {
    stored = JSON.parse(localStorage.getItem(LS_KEY) ?? '{}');
  } catch {
    stored = {};
  }

  // lang LUÔN ưu tiên URL (?lang=) — front-end điều khiển bằng URL, xem AGENT.md mục 6
  const urlLang = new URLSearchParams(location.search).get('lang');
  if (urlLang) stored.lang = urlLang;

  /** @type {Record<string, any>} */
  const state = { ...defaults, ...stored };

  const persist = () => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(state));
    } catch {
      /* private mode — bỏ qua, không phải lỗi */
    }
  };

  return {
    setDefault(key, value) {
      // nếu học sinh đã chọn thì KHÔNG ghi đè
      if (Object.prototype.hasOwnProperty.call(stored, key)) return;
      state[key] = value;
      opts.emitter.emit(`settings:${key}`, value);
    },

    get(key) {
      return state[key];
    },

    set(key, value, o = {}) {
      if (state[key] === value) return;
      state[key] = value;
      stored[key] = value;
      if (o.persist !== false) persist();
      opts.emitter.emit(`settings:${key}`, value);
      opts.emitter.emit('settings:change', { key, value });
    },

    onChange(key, cb) {
      return opts.emitter.on(`settings:${key}`, cb);
    },

    all() {
      return { ...state };
    },
  };
}
